import { Button, message, Popconfirm, Rate, Result } from "antd";
import debounce from "lodash/debounce";
import { FC, useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Icon from "../assets/icons/icon";
import { renderErrorMessage } from "../components/messages/errorMessage";
import DataTable from "../components/table/dataTable";
import { CustomColumnsType, RenderFunc } from "../components/table/dataTableTypes";
import UserStore from "../contexts/userStore";
import _t from "../lang/translate";
import PageTitle from "../layout/pageTitle";
import { getPath } from "../routes/appRoutes";
import { deleteMerchant, merchantsUrl } from "../services/merchantService";
import { pageSizeKey, searchKey } from "../services/urlQueryService";
import { useApi } from "../services/useApi";
import { PaginationedData } from "../types/apiTypes";
import { IdType, LocationStateType, ScreenProps } from "../types/appTypes";
import { MerchantType } from "../types/merchantTypes";

const Merchants: FC<ScreenProps> = ({ title }) => {
  const location = useLocation<LocationStateType>();
  const [refreshId, setRefreshId] = useState<string | undefined>();
  const { hasPermission } = UserStore.useContainer();
  const [{ data, isLoading, isError }, setUrl, setData] = useApi<PaginationedData<MerchantType>>("", { data: [] });

  // If doing as it recommends, table search stops working
  const delayedSetUrl = useCallback(
    debounce((url: string) => {
      setUrl(url);
    }, 200), [setUrl]
  );

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    if (typeof refreshId === "string") {
      query.set("refreshId", refreshId);
    }
    query.set(pageSizeKey, localStorage.getItem(pageSizeKey) || "20");
    const url = merchantsUrl(query.toString());
    query.has(searchKey) ? delayedSetUrl(url) : setUrl(url);
  }, [setUrl, location.search, refreshId, delayedSetUrl]);

  const handleDelete = async (id: IdType) => {
    const originalData = { ...data };
    try {
      setData((state) => ({
        ...state,
        data: originalData.data.filter((item) => item.id !== id),
      }));
      await deleteMerchant(id);
      message.success(_t("deleted"));
    } catch (ex) {
      setData(originalData);
      renderErrorMessage(ex, _t("msg.not_deleted"))
    }
  };

  const columns: CustomColumnsType<MerchantType> = [
    {
      title: "#",
      align: "center",
      key: "id",
      sorter: true,
      dataIndex: "id",
      width: 110,
      render: (text, record) =>
        hasPermission("screen.merchant") ? (
          <Link to={getPath("merchant", record.id)}>
            <strong>{text}</strong>
          </Link>
        ) : (
          text
        ),
      fixed: "left",
    },
    {
      title: _t("name"),
      key: "name",
      dataIndex: "name",
      sorter: true,
      ellipsis: true,
    },
    {
      title: _t("address"),
      key: "address",
      dataIndex: "address",
      sorter: true,
      hidden: true,
      ellipsis: true,
    },
    {
      title: _t("zip", "city", " & "),
      key: "zip",
      sorter: true,
      dataIndex: "zip",
      render: (text, record) => `${text ? text : ""} ${record.city ? record.city : ""}`,
    },
    {
      title: _t("email"),
      key: "email",
      sorter: true,
      dataIndex: "email",
    },
    {
      title: _t("phone"),
      key: "phone",
      dataIndex: "phone",
      sorter: true,
    },
    {
      title: _t("review"),
      key: "rating",
      dataIndex: "rating",
      sorter: true,
      render: (text) => <Rate allowHalf disabled value={text} />,
    },
    {
      title: _t("country"),
      key: "country",
      sorter: true,
      dataIndex: "country",
    },
  ];

  const tableActions: RenderFunc<MerchantType> = (_, record) => {
    return hasPermission("merchant.delete") ? (
      <Popconfirm
        placement="topLeft"
        onConfirm={() => handleDelete(record.id)}
        icon={<Icon fill="red" name="information-circle-outline" />}
        title={
          <div>
            {_t("msg.confirm_delete")}&nbsp;
            <strong>
              {record.id} : {record.name}
            </strong>
          </div>
        }
      >
        <Button className="muted delete-btn" type="text" shape="circle" icon={<Icon name="trash-outline" />} />
      </Popconfirm>
    ) : null;
  };

  const handleRefresh = () => setRefreshId(new Date().getSeconds().toString());

  return (
    <>
      <PageTitle
        fluid
        title={title}
        extra={
          hasPermission("merchant.create") && (
            <Button key="1" type="primary">
              <Link to={getPath("merchant", "new")}>{_t("new_merchant")}</Link>
            </Button>
          )
        }
      />
      {isError ? (
        <Result status="error" title={_t("msg.unknown_error")} />
      ) : (
        <DataTable<MerchantType>
          loading={isLoading}
          onRefresh={handleRefresh}
          renderActions={tableActions}
          columnStorageKey="MERCHANT"
          columns={columns}
          dataSource={data.data}
          meta={data.meta}
        />
      )}
    </>
  );
};

export default Merchants;
